import { z } from 'zod'
import { addDays, isoDayOfWeek, type DayId } from './calendar.ts'
import type { CommandContext, CommandResult, DomainError } from './commands.ts'
import {
  archiveTask,
  defineReward,
  defineTask,
  renameTask,
  repriceReward,
  updateSteps,
} from './commands.ts'
import type { DomainEvent, Step } from './events.ts'
import { project } from './projections/project.ts'
import { missionLadder, routineLadder } from './seed.ts'
import type { FamilyState, Member, Reward, Task } from './state.ts'
import { childrenOf } from './state.ts'
import type { MemberId, RewardId, StepId, TaskId } from './ids.ts'

/**
 * A plan is what an assistant is allowed to say.
 *
 * The rule the whole file exists to enforce: nothing outside this house decides
 * anything. An assistant produces a *proposal*, in a vocabulary narrow enough to
 * be checked; every proposal is then run through the same commands a parent's
 * own taps go through, against the same state, with the same refusals. A model
 * that proposes a routine paying coins gets `routine-cannot-pay`, exactly as a
 * parent tapping it would. It cannot write to the log, it cannot delete
 * anything, and it cannot reach a child's screen without a parent reading the
 * sentence first.
 *
 * Proposals name things the way a parent does — "Gabriel", "Rutina de la
 * mañana" — and never carry identifiers. A name that does not resolve is a
 * rejected proposal, not a new object, which is what stops a hallucinated task
 * from quietly appearing.
 */

const name = z.string().trim().min(1).max(60)
const stepText = z.string().trim().min(1).max(80)

export const ProposalSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('create-task'),
    child: name,
    kind: z.enum(['routine', 'mission']),
    moment: z.enum(['morning', 'afternoon', 'evening']).optional(),
    name,
    steps: z.array(stepText).max(8).default([]),
    coins: z.int().min(0).max(50).default(0),
    xp: z.int().min(0).max(100).default(10),
  }),
  z.object({ op: z.literal('rename-task'), child: name, name, newName: name }),
  z.object({ op: z.literal('set-steps'), child: name, name, steps: z.array(stepText).max(8) }),
  z.object({ op: z.literal('remove-task'), child: name, name, reason: z.string().trim().max(140) }),
  z.object({
    op: z.literal('create-reward'),
    name,
    price: z.int().min(0).max(500),
    kind: z.enum(['screen', 'privilege', 'family', 'social', 'big']),
    children: z.array(name).min(1),
  }),
  z.object({ op: z.literal('set-price'), name, newPrice: z.int().min(0).max(500) }),
])

export type Proposal = z.infer<typeof ProposalSchema>

/** What an assistant is asked to return. Anything else is discarded whole. */
export const PlanSchema = z.object({ changes: z.array(ProposalSchema).max(20) })

export interface PlannedChange {
  readonly proposal: Proposal
  /** Empty when the domain refused it. */
  readonly events: readonly DomainEvent[]
  readonly error: DomainError | undefined
}

// --- Resolving the names a parent would use ----------------------------------

/** Lowercase, unaccented, collapsed spaces: "MAÑANA " and "manana" are one. */
function key(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

function childNamed(state: FamilyState, value: string): Member | undefined {
  return childrenOf(state).find((m) => key(m.name) === key(value))
}

function taskNamed(state: FamilyState, memberId: MemberId, value: string): Task | undefined {
  return [...state.tasks.values()].find(
    (t) => t.memberId === memberId && !t.archived && key(t.name) === key(value),
  )
}

function rewardNamed(state: FamilyState, value: string): Reward | undefined {
  return [...state.rewards.values()].find((r) => r.active && key(r.name) === key(value))
}

/** Steps arrive as plain sentences; they need stable ids to be ticked. */
function stepsFrom(texts: readonly string[], newId: () => string): Step[] {
  return texts.map((text) => ({ id: newId() as StepId, text }))
}

/**
 * The next weekly meeting, which is when a price is allowed to move. A price
 * that changes the same evening a child was saving for something teaches that
 * saving is pointless, so the command schedules rather than applies.
 */
export function nextMeetingDay(state: FamilyState, from: DayId): DayId {
  const meeting = state.family?.meetingDayOfWeek ?? 7
  const ahead = (meeting - isoDayOfWeek(from) + 7) % 7
  return addDays(from, ahead === 0 ? 7 : ahead)
}

// --- Turning one proposal into a command -------------------------------------

function runProposal(state: FamilyState, proposal: Proposal, ctx: CommandContext): CommandResult {
  switch (proposal.op) {
    case 'create-task': {
      const child = childNamed(state, proposal.child)
      if (!child) return refuse('unknown-member')
      // A routine earns experience and never coins. The ladders come from the
      // same file the seed uses, so an assistant cannot invent a pay curve.
      const rewards =
        proposal.kind === 'routine'
          ? routineLadder(proposal.xp)
          : missionLadder(proposal.coins, proposal.xp)
      return defineTask(
        state,
        {
          taskId: ctx.newId() as TaskId,
          memberId: child.id,
          kind: proposal.kind,
          ...(proposal.moment !== undefined ? { moment: proposal.moment } : {}),
          name: proposal.name,
          steps: stepsFrom(proposal.steps, ctx.newId),
          difficulty: 2,
          rewards,
          promotionThreshold: 10,
          requiresPhoto: false,
        },
        ctx,
      )
    }

    case 'rename-task': {
      const child = childNamed(state, proposal.child)
      if (!child) return refuse('unknown-member')
      const task = taskNamed(state, child.id, proposal.name)
      if (!task) return refuse('unknown-task')
      return renameTask(state, { taskId: task.id, name: proposal.newName }, ctx)
    }

    case 'set-steps': {
      const child = childNamed(state, proposal.child)
      if (!child) return refuse('unknown-member')
      const task = taskNamed(state, child.id, proposal.name)
      if (!task) return refuse('unknown-task')
      return updateSteps(
        state,
        { taskId: task.id, steps: stepsFrom(proposal.steps, ctx.newId) },
        ctx,
      )
    }

    case 'remove-task': {
      const child = childNamed(state, proposal.child)
      if (!child) return refuse('unknown-member')
      const task = taskNamed(state, child.id, proposal.name)
      if (!task) return refuse('unknown-task')
      return archiveTask(state, { taskId: task.id, reason: proposal.reason }, ctx)
    }

    case 'create-reward': {
      const members: MemberId[] = []
      for (const childName of proposal.children) {
        const child = childNamed(state, childName)
        if (!child) return refuse('unknown-member')
        members.push(child.id)
      }
      return defineReward(
        state,
        {
          rewardId: ctx.newId() as RewardId,
          name: proposal.name,
          price: proposal.price,
          kind: proposal.kind,
          memberIds: members,
        },
        ctx,
      )
    }

    case 'set-price': {
      const reward = rewardNamed(state, proposal.name)
      if (!reward) return refuse('unknown-reward')
      return repriceReward(
        state,
        {
          rewardId: reward.id,
          newPrice: proposal.newPrice,
          effectiveFrom: nextMeetingDay(state, ctx.day),
        },
        ctx,
      )
    }
  }
}

function refuse(code: DomainError['code']): CommandResult {
  return { ok: false, error: { code } }
}

/**
 * Walks the proposals in order against a state that moves as they are accepted,
 * so "create this task" followed by "and split its second step" both land. A
 * refused proposal changes nothing and does not stop the ones after it — the
 * parent sees the whole list with each line's verdict beside it.
 */
export function planChanges(
  state: FamilyState,
  proposals: readonly Proposal[],
  ctx: CommandContext,
  log: readonly DomainEvent[],
): readonly PlannedChange[] {
  const planned: PlannedChange[] = []
  let events = [...log]
  let current = state

  for (const proposal of proposals) {
    const result = runProposal(current, proposal, ctx)
    if (result.ok) {
      events = [...events, ...result.events]
      current = project(events)
      planned.push({ proposal, events: result.events, error: undefined })
    } else {
      planned.push({ proposal, events: [], error: result.error })
    }
  }

  return planned
}
